import Chip from '@mui/material/Chip';
import type { SxProps } from '@mui/material/styles';

// Monochrome status language: solid white = good/terminal, bright outline = active,
// dim outline = neutral, heavy black-on-white outline = bad. The label carries the
// specifics; the weight makes it scannable.
type Tone = 'good' | 'active' | 'neutral' | 'warn' | 'bad';

const TONES: Record<string, Tone> = {
  succeeded: 'good',
  done: 'good',
  finished: 'good',
  running: 'active',
  queued: 'neutral',
  skipped: 'neutral',
  pending: 'warn',
  downgraded: 'warn',
  canceled: 'warn',
  failed: 'bad',
  error: 'bad'
};

const TONE_SX: Record<Tone, SxProps> = {
  good: { bgcolor: 'text.primary', color: 'background.default' },
  active: {
    bgcolor: 'transparent',
    color: 'text.primary',
    border: 1,
    borderColor: 'text.primary'
  },
  neutral: {
    bgcolor: 'transparent',
    color: 'text.secondary',
    border: 1,
    borderColor: 'divider'
  },
  warn: {
    bgcolor: 'transparent',
    color: 'text.secondary',
    border: 1,
    borderColor: 'text.secondary',
    fontStyle: 'italic'
  },
  bad: {
    bgcolor: 'transparent',
    color: 'text.primary',
    border: 2,
    borderColor: 'text.primary',
    fontWeight: 800,
    textDecoration: 'underline'
  }
};

/**
 * `label` reads differently from the status that picks the tone; it defaults to the status itself.
 * `title` is the longer story behind the label, shown on hover.
 */
export const StatusChip = ({
  status,
  label,
  title
}: {
  status: string | null | undefined;
  label?: string;
  title?: string;
}) => {
  if (!status) return null;
  return (
    <Chip label={label ?? status} title={title} size="small" sx={TONE_SX[TONES[status] ?? 'neutral']} />
  );
};

export default StatusChip;
